import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

const SharedNoteView = ({ isOpen, onClose, message, groupId, onSave }) => {
  const { t } = useTranslation('notes');
  const noteTitle = message?.note_title || message?.noteTitle;
  const displayTitle = noteTitle && noteTitle.trim() ? noteTitle.trim() : t('common:unnamed');
  const noteContent = message?.note_content || message?.noteContent || '';
  const noteTitleColor = message?.note_title_color || message?.noteTitleColor || '#ffffff';

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleSaveClick = () => {
    if (onSave) {
      onSave();
    }
  };

  if (!isOpen || !message) return null;

  // Use portal to render outside normal DOM hierarchy
  return createPortal(
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop - Full screen overlay with blur */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-[100] bg-slate-900/30 backdrop-blur-sm flex items-center justify-center"
              style={{ pointerEvents: 'auto' }}
            />

            {/* Modal Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-[1000000] flex items-center justify-center p-4 overflow-y-auto pointer-events-none"
              onClick={onClose}
              style={{ pointerEvents: 'auto' }}
            >
              <div
                className={`bg-white dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-white/10`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-[#3d8ef0] dark:text-[#7ab8f8]" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t('sharedNoteView.title')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('sharedNoteView.readOnlyMode')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {onSave && (
                      <button
                        onClick={handleSaveClick}
                        className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                      >
                        {t('common:save')}
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#1e2d47] transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {/* Title */}
                  <div 
                    className="mb-6 p-4 rounded-lg"
                    style={{
                      backgroundColor: noteTitleColor !== '#ffffff' 
                        ? `${noteTitleColor}20` 
                        : 'rgba(59, 130, 246, 0.1)',
                      borderLeft: `4px solid ${noteTitleColor}`,
                    }}
                  >
                    <h2 
                      className="text-2xl font-bold text-gray-900 dark:text-white break-words"
                      style={{ color: noteTitleColor !== '#ffffff' ? noteTitleColor : undefined }}
                    >
                      {displayTitle}
                    </h2>
                  </div>

                  {/* Content */}
                  <div 
                    className="text-gray-900 dark:text-gray-100"
                    style={{
                      lineHeight: '1.8',
                      fontSize: '16px',
                    }}
                  >
                    {noteContent ? (
                      <div 
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(noteContent, { USE_PROFILES: { html: true } }) }}
                        className="shared-note-content"
                        style={{
                          wordBreak: 'break-word',
                        }}
                      />
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 italic">{t('sharedNoteView.emptyContent')}</p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
};

export default SharedNoteView;
